// frontend/src/site/templates/TemplateCorretorLovable/components/HeroSection.jsx
import React from 'react';
import { ArrowRight, Phone } from 'lucide-react';
import { Button } from '../ui/button';

function normalizePhoneToWa(phone) {
  const raw = String(phone || '').trim();
  if (!raw) return null;

  const digits = raw.replace(/\D/g, '');
  const wa = digits.startsWith('55') ? digits : `55${digits}`;
  if (wa.length < 12) return null;
  return wa;
}

export default function HeroSection({ siteConfig, slug }) {
  const nome = siteConfig?.nome || siteConfig?.displayName || 'Corretor';
  const creci = siteConfig?.creci || '';
  const foto = siteConfig?.fotoUrl || '';

  const headline =
    siteConfig?.headline || 'Especialista em imóveis e oportunidades';
  const subheadline =
    siteConfig?.subheadline ||
    'Atendimento consultivo e segurança em cada negociação.';

  const waNumber = normalizePhoneToWa(siteConfig?.whatsapp);
  const waHref = waNumber
    ? `https://wa.me/${waNumber}?text=${encodeURIComponent(
        `Olá ${nome}! Vim pelo seu site e gostaria de mais informações.`
      )}`
    : null;

  return (
    <section className="relative min-h-screen flex items-center pt-20 bg-beige-dark">
      <div className="container mx-auto px-4 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Content */}
          <div className="order-2 lg:order-1 space-y-8 animate-fade-up">
            <div className="space-y-4">
              <p className="text-gold font-medium tracking-wider text-sm uppercase">
                Corretor de Imóveis {creci ? `| CRECI ${creci}` : ''}
              </p>

              <h1 className="font-serif text-5xl lg:text-7xl font-semibold text-foreground leading-tight">
                {nome}
              </h1>
            </div>

            <h2 className="font-serif text-2xl lg:text-3xl text-slate-medium font-normal italic">
              {headline}
            </h2>

            <p className="text-lg text-muted-foreground max-w-lg leading-relaxed">
              {subheadline}
            </p>

            <div className="flex flex-col sm:flex-row gap-4 pt-4">
              {waHref ? (
                <a href={waHref} target="_blank" rel="noreferrer">
                  <Button variant="gold" size="xl" className="group gap-2">
                    <Phone className="w-5 h-5" />
                    Falar no WhatsApp
                  </Button>
                </a>
              ) : (
                <Button
                  variant="gold"
                  size="xl"
                  className="group gap-2"
                  disabled
                  title="WhatsApp não configurado"
                >
                  <Phone className="w-5 h-5" />
                  Falar no WhatsApp
                </Button>
              )}

              <Button
                variant="heroOutline"
                size="xl"
                className="group"
                onClick={() => window.location.assign(`/site/${slug}/imoveis`)}
              >
                Ver Imóveis
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Button>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-8 pt-8 border-t border-border">
              <div>
                <p className="font-serif text-4xl font-semibold text-foreground">
                  15+
                </p>
                <p className="text-sm text-muted-foreground mt-1">
                  Anos de experiência
                </p>
              </div>
              <div>
                <p className="font-serif text-4xl font-semibold text-foreground">
                  500+
                </p>
                <p className="text-sm text-muted-foreground mt-1">
                  Imóveis negociados
                </p>
              </div>
              <div>
                <p className="font-serif text-4xl font-semibold text-foreground">
                  98%
                </p>
                <p className="text-sm text-muted-foreground mt-1">
                  Clientes satisfeitos
                </p>
              </div>
            </div>
          </div>

          {/* Image */}
          <div className="order-1 lg:order-2 flex justify-center lg:justify-end animate-fade-in">
            <div className="relative">
              <div className="absolute -top-4 -right-4 w-full h-full border-2 border-gold/30 rounded-2xl" />
              <div className="absolute -bottom-4 -left-4 w-32 h-32 bg-gold/10 rounded-full blur-3xl" />

              <div className="relative overflow-hidden rounded-2xl shadow-medium bg-muted">
                {foto ? (
                  <img
                    src={foto}
                    alt={`${nome} - Corretor de Imóveis`}
                    className="w-full max-w-md lg:max-w-lg h-auto object-cover"
                    loading="lazy"
                  />
                ) : (
                  <div className="w-[340px] h-[440px] max-w-md lg:max-w-lg flex items-center justify-center">
                    <span className="text-muted-foreground text-sm">
                      Sem foto
                    </span>
                  </div>
                )}

                <div className="absolute inset-0 pointer-events-none bg-gradient-to-t from-black/10 to-transparent" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
